// Training seed->key algorithms for the Security Access Simulator. Every algorithm here
// is deliberately fictional and documented as such — this file must never contain a real
// OEM/vehicle unlock algorithm. New training algorithms can be added to ALGORITHMS
// without touching any UI code, as long as they implement `trace(seed, param, opts)`.
import { toUint32, toHexBytes, rotl32, crc32 } from './hexUtils';

export const ALGORITHM_IDS = {
  XOR: 'xor',
  XOR_ADD: 'xor_add',
  ROTATE: 'rotate',
  SUBTRACT: 'subtract',
  CRC32: 'crc32',
  CUSTOM: 'custom',
};

// A `trace` implementation returns { keyNum, rows }, where `rows` is an ordered list of
// { opLabel, operandBytes, resultBytes } describing each step of the visualizer. `null`
// operandBytes means the step has no second operand to show (e.g. a rotation amount, or a
// whole-seed CRC).
const xorTrace = (seed, param) => {
  const result = toUint32(seed ^ param);
  return { keyNum: result, rows: [{ opLabel: 'XOR', operandBytes: toHexBytes(param), resultBytes: toHexBytes(result) }] };
};

const xorAddTrace = (seed, param) => {
  const afterXor = toUint32(seed ^ param);
  const result = toUint32(afterXor + param);
  return {
    keyNum: result,
    rows: [
      { opLabel: 'XOR', operandBytes: toHexBytes(param), resultBytes: toHexBytes(afterXor) },
      { opLabel: '+', operandBytes: toHexBytes(param), resultBytes: toHexBytes(result) },
    ],
  };
};

const rotateTrace = (seed, param, opts) => {
  const n = Number.isFinite(opts?.rotation) ? opts.rotation : 8;
  const rotated = rotl32(seed, n);
  const result = toUint32(rotated ^ param);
  return {
    keyNum: result,
    rows: [
      { opLabel: `ROTL ${n}`, operandBytes: null, resultBytes: toHexBytes(rotated) },
      { opLabel: 'XOR', operandBytes: toHexBytes(param), resultBytes: toHexBytes(result) },
    ],
  };
};

const subtractTrace = (seed, param) => {
  const result = toUint32(seed - param);
  return { keyNum: result, rows: [{ opLabel: '-', operandBytes: toHexBytes(param), resultBytes: toHexBytes(result) }] };
};

const crc32Trace = (seed, param) => {
  const seedBytesNum = toHexBytes(seed).map((h) => parseInt(h, 16));
  const crc = crc32(seedBytesNum);
  const result = toUint32(crc ^ param);
  return {
    keyNum: result,
    rows: [
      { opLabel: 'CRC32(seed bytes)', operandBytes: null, resultBytes: toHexBytes(crc) },
      { opLabel: 'XOR', operandBytes: toHexBytes(param), resultBytes: toHexBytes(result) },
    ],
  };
};

const customTrace = (seed, param, opts) => {
  const expr = opts?.customFormula ?? '';
  const result = toUint32(evalSafeExpression(expr, { seed, param }));
  return { keyNum: result, rows: [{ opLabel: expr || '(empty formula)', operandBytes: null, resultBytes: toHexBytes(result) }] };
};

export const ALGORITHMS = [
  { id: ALGORITHM_IDS.XOR, label: 'XOR', formula: 'key = seed ^ param', trace: xorTrace },
  { id: ALGORITHM_IDS.XOR_ADD, label: 'XOR + Addition', formula: 'key = (seed ^ param) + param', trace: xorAddTrace },
  { id: ALGORITHM_IDS.ROTATE, label: 'Bit Rotation', formula: 'key = rotl32(seed, n) ^ param', trace: rotateTrace, hasRotation: true },
  { id: ALGORITHM_IDS.SUBTRACT, label: 'Subtraction', formula: 'key = (seed - param) & 0xFFFFFFFF', trace: subtractTrace },
  { id: ALGORITHM_IDS.CRC32, label: 'CRC-Based', formula: 'key = CRC32(seed bytes) ^ param', trace: crc32Trace },
  { id: ALGORITHM_IDS.CUSTOM, label: 'Custom Formula', formula: 'user-defined', trace: customTrace, isCustom: true },
];

export const ALGORITHM_BY_ID = Object.fromEntries(ALGORITHMS.map((a) => [a.id, a]));

// Computes the full step-by-step visualizer trace for the given algorithm. Throws on a
// malformed custom formula — callers (the visualizer, the engine's key computation) should
// catch this and surface it as a validation error rather than letting it crash the render.
export const computeTrace = (algorithmId, seed, param, opts) => {
  const algorithm = ALGORITHM_BY_ID[algorithmId];
  if (!algorithm) throw new Error(`Unknown algorithm "${algorithmId}"`);
  return algorithm.trace(toUint32(seed), toUint32(param), opts);
};

export const computeKey = (algorithmId, seed, param, opts) => computeTrace(algorithmId, seed, param, opts).keyNum;

// ---------------------------------------------------------------------------------------
// Safe custom-formula expression parser/evaluator.
//
// Deliberately NOT `eval`/`Function` — this is a small hand-rolled recursive-descent
// parser that only ever resolves the two identifiers `seed` and `param`, over the operator
// set `+ - ^ & | << >> ~` and parenthesized/hex/decimal literals. There is no way for a
// formula string to reach any JS runtime object, call a function, or read anything outside
// the two provided variables.
// ---------------------------------------------------------------------------------------

const TOKEN_RE = /\s*(0[xX][0-9a-fA-F]+|\d+|seed|param|<<|>>|[+\-^&|~()])/g;

function tokenize(expr) {
  const tokens = [];
  let match;
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(expr))) {
    if (match.index !== lastIndex) {
      throw new Error(`Unexpected character near "${expr.slice(lastIndex, match.index + 1)}"`);
    }
    tokens.push(match[1]);
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex !== expr.length) {
    throw new Error(`Unexpected character near "${expr.slice(lastIndex)}"`);
  }
  if (tokens.length === 0) throw new Error('Formula is empty.');
  return tokens;
}

// Precedence (high to low), matching JS so the formula reads the way a user expects:
// unary ~ > additive (+ -) > shift (<< >>) > & > ^ > |.
function parseTokens(tokens, vars) {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];
  const expect = (t) => {
    if (next() !== t) throw new Error(`Expected "${t}"`);
  };

  const parsePrimary = () => {
    const t = peek();
    if (t === undefined) throw new Error('Unexpected end of expression.');
    if (t === '(') {
      next();
      const v = parseOr();
      expect(')');
      return v;
    }
    if (t === 'seed' || t === 'param') {
      next();
      return vars[t];
    }
    if (/^0[xX]/.test(t)) {
      next();
      return parseInt(t, 16) >>> 0;
    }
    if (/^\d+$/.test(t)) {
      next();
      return parseInt(t, 10) >>> 0;
    }
    throw new Error(`Unexpected token "${t}".`);
  };
  const parseUnary = () => {
    if (peek() === '~') {
      next();
      return (~parseUnary()) >>> 0;
    }
    return parsePrimary();
  };
  const parseAdditive = () => {
    let v = parseUnary();
    while (peek() === '+' || peek() === '-') {
      const op = next();
      const rhs = parseUnary();
      v = op === '+' ? (v + rhs) >>> 0 : (v - rhs) >>> 0;
    }
    return v;
  };
  const parseShift = () => {
    let v = parseAdditive();
    while (peek() === '<<' || peek() === '>>') {
      const op = next();
      const rhs = parseAdditive();
      v = op === '<<' ? (v << rhs) >>> 0 : (v >>> rhs) >>> 0;
    }
    return v;
  };
  const parseAnd = () => {
    let v = parseShift();
    while (peek() === '&') {
      next();
      v = (v & parseShift()) >>> 0;
    }
    return v;
  };
  const parseXor = () => {
    let v = parseAnd();
    while (peek() === '^') {
      next();
      v = (v ^ parseAnd()) >>> 0;
    }
    return v;
  };
  function parseOr() {
    let v = parseXor();
    while (peek() === '|') {
      next();
      v = (v | parseXor()) >>> 0;
    }
    return v;
  }

  const result = parseOr();
  if (pos !== tokens.length) throw new Error(`Unexpected trailing token "${tokens[pos]}".`);
  return result;
}

export const evalSafeExpression = (expr, vars) => {
  const tokens = tokenize(expr);
  return toUint32(parseTokens(tokens, vars));
};

// Non-throwing wrapper for inline validation UI (e.g. as the user types a custom formula).
export const validateCustomFormula = (expr) => {
  try {
    evalSafeExpression(expr, { seed: 0, param: 0 });
    return { valid: true, error: null };
  } catch (err) {
    return { valid: false, error: err.message };
  }
};
